import React, { useState } from "react";
import { Box, Select, MenuItem } from "@mui/material";
import { useSelector } from "react-redux";
import sh from "bundles/common/utils/sh";
import BelgiumFlag from "icons/belgium-flag.svg";
import FranceFlag from "icons/france-flag.svg";
import UkFlag from "icons/uk-flag.svg";

export default function SelectLanguage({ initLocale = "fr", inline = false, compact = false }) {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const [locale, setLocale] = useState(i18nLocale || initLocale);

  const handleMobileLanguageSelection = (language) => {
    handleLanguageSelection(language);
  };

  const handleDesktopLanguageSelection = (event) => {
    const language = event.target.value;
    handleLanguageSelection(language);
  };

  const redirectToPreferredLocale = (language) => {
    if (window) {
      let path = window.location?.pathname?.split("/");
      let oldLocale = path?.[1];
      if (languages.includes(oldLocale)) {
        window.location.href = `${window.location.origin}/${language}/${path
          .slice(2)
          .join("/")}${window.location.search}`;
      }
    }
  };

  const handleLanguageSelection = (language) => {
    setLocale(language);
    if (currentRecruiter) {
      sh.put("recruiter", {
        authenticity_token: authenticityToken,
        recruiter: {
          id: currentRecruiter.id,
          preferred_locale: language,
        }
      }).then(() => {
        redirectToPreferredLocale(language);
      }).catch((err) => {
        console.error(err);
      });
    } else if (currentCandidate) {
      sh.put("candidate", {
        authenticity_token: authenticityToken,
        candidate: {
          id: currentCandidate.id,
          preferred_locale: language,
        }
      }).then(() => {
        redirectToPreferredLocale(language);
      }).catch((err) => {
        console.error(err);
      });
    } else {
      redirectToPreferredLocale(language);
    }
  };

  const languages = ["fr", "fr-fr", "en", "nl"];
  const languageMenuLabels = {
    fr: "Français (BE)",
    "fr-fr": "Français (FR)",
    en: "English",
    nl: "Nederlands",
  };
  const languageShortLabel = {
    fr: "FR",
    "fr-fr": "FR",
    en: "EN",
    nl: "NL",
  };
  const flagByLocale = {
    fr: BelgiumFlag,
    "fr-fr": FranceFlag,
    en: UkFlag,
    nl: BelgiumFlag,
  };

  const inlineContainerSx = compact
    ? {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 0.75,
      width: "100%",
      maxWidth: "100%",
      boxSizing: "border-box",
      px: 0.5,
    }
    : {
      display: "flex",
      width: "100%",
      justifyContent: "space-evenly",
      gap: 1,
      flexWrap: "wrap",
    };

  return inline ? (
    <Box sx={inlineContainerSx}>
      {languages.map((language) => {
        return (
          <Box
            key={`key-${language}`}
            sx={{
              textTransform: "uppercase",
              backgroundColor: "#fff",
              padding: compact ? "5px 8px" : "6px 12px",
              border:
                language === i18nLocale
                  ? "1px solid hsl(153 68% 25%)"
                  : "1px solid rgba(2, 51, 49, 0.12)",
              borderRadius: compact ? "8px" : "10px",
              cursor: "pointer",
              fontSize: compact ? 11 : 13,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: compact ? 0.5 : 0.75,
              fontFamily: language === i18nLocale ? "Segoe UI Semibold, sans-serif" : "Segoe UI Regular, sans-serif",
              color: language === i18nLocale ? "hsl(153 68% 25%)" : "hsl(178 93% 10%)",
              transition: "all 150ms ease",
              minWidth: 0,
              "&:hover": {
                backgroundColor: "hsl(153 68% 25% / 0.08)",
              },
            }}
            onClick={() => handleMobileLanguageSelection(language)}
          >
            <Box
              component="img"
              src={flagByLocale[language]}
              alt={`${language} flag`}
              sx={{
                width: compact ? "14px" : "16px",
                height: compact ? "10px" : "12px",
                flexShrink: 0,
              }}
            />
            {languageShortLabel[language] ?? language}
          </Box>
        );
      })}
    </Box>
  ) : (
    <Select
      id="language-select"
      value={locale}
      onChange={handleDesktopLanguageSelection}
      size="small"
      renderValue={(value) => (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontFamily: "Segoe UI Semibold, sans-serif" }}>
          <Box
            component="img"
            src={flagByLocale[value]}
            alt={`${value} flag`}
            sx={{ width: "16px", height: "12px" }}
          />
          <Box component="span">{languageShortLabel[value] ?? value}</Box>
        </Box>
      )}
      MenuProps={{
        disableScrollLock: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "right",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        PaperProps: {
          sx: {
            mt: 0.75,
            borderRadius: "12px",
            border: "1px solid rgba(2, 51, 49, 0.12)",
            boxShadow: "0 10px 24px rgba(2, 51, 49, 0.12)",
            overflow: "hidden",
          },
        },
      }}
      sx={{
        height: 36,
        borderRadius: "12px",
        backgroundColor: "#fff",
        fontFamily: "Segoe UI Semibold, sans-serif",
        fontSize: 14,
        color: "hsl(178 93% 10%)",
        border: "1px solid rgba(2, 51, 49, 0.12)",
        boxShadow: "0 2px 10px rgba(2, 51, 49, 0.06)",
        "& .MuiSelect-select": {
          py: "6px",
          px: "10px",
          pr: "28px !important",
          textTransform: "uppercase",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "&:hover": {
          borderColor: "rgba(22, 109, 76, 0.35)",
          boxShadow: "0 6px 14px rgba(22, 109, 76, 0.12)",
        },
      }}
    >
      {languages.map((language) => (
        <MenuItem
          value={language}
          key={language}
          sx={{
            justifyContent: "flex-start",
            textTransform: "none",
            borderRadius: "8px",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif",
            color: language === locale ? "hsl(153 68% 25%)" : "hsl(178 93% 10%)",
            my: 0.25,
            mx: 0.5,
            gap: 0.25,
            pl: 1.5,
            pr: 1.5,
            "&:hover": {
              backgroundColor: "hsl(153 68% 25% / 0.08)",
            },
          }}
          disableGutters
        >
          <Box
            component="img"
            src={flagByLocale[language]}
            alt={`${language} flag`}
            sx={{ width: "16px", height: "12px", mr: 0.25 }}
          />
          <Box component="span">{languageMenuLabels[language] ?? language}</Box>
        </MenuItem>
      ))}
    </Select>
  );
}
